import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { FaPhone, FaCommentDots } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';
import { getTranslation } from '../../translations/presentation';
import {
  ProductInfo,
  PriceHighlight,
  CreditHighlight,
  PlatformRequirements,
  BonusTag,
  PlatformTitle,
  ServicesGrid,
  ServiceCard,
  ServiceName,
  ServicePrice,
  LogoContainer,
  LeadCostNote
} from '../../components/checkout/CheckoutStyles';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInFromTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromBottom = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const glitchEffect = keyframes`
  0% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  2% {
    clip-path: inset(10% 0 60% 0);
    transform: translate(-2px, 2px);
  }
  4% {
    clip-path: inset(30% 0 40% 0);
    transform: translate(2px, -2px);
  }
  6% {
    clip-path: inset(50% 0 10% 0);
    transform: translate(-1px, 1px);
  }
  8% {
    clip-path: inset(10% 0 70% 0);
    transform: translate(1px, -1px);
  }
  10% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
  100% {
    clip-path: inset(0 0 0 0);
    transform: translate(0);
  }
`;

const scanLine = keyframes`
  0% {
    transform: translateY(-100%);
  }
  50% {
    transform: translateY(100%);
  }
  100% {
    transform: translateY(-100%);
  }
`;

// Nova animação de pulso para o prompt de clique
const pulseAnimation = keyframes`
  0% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.7;
    transform: scale(1);
  }
`;

// Nova animação para o efeito de brilho
const glowPulse = keyframes`
  0% {
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 30px rgba(100, 182, 255, 0.8);
  }
  100% {
    box-shadow: 0 0 10px rgba(100, 182, 255, 0.3);
  }
`;

// Animação de partículas flutuantes
const floatingParticles = keyframes`
  0% {
    transform: translateY(0) translateX(0) rotate(0);
  }
  25% {
    transform: translateY(-10px) translateX(5px) rotate(5deg);
  }
  50% {
    transform: translateY(0) translateX(10px) rotate(0);
  }
  75% {
    transform: translateY(10px) translateX(5px) rotate(-5deg);
  }
  100% {
    transform: translateY(0) translateX(0) rotate(0);
  }
`;

// Animação de dados digitais
const dataFlow = keyframes`
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 100% 100%;
  }
`;

// Animação de brilho intenso para textos
const textGlowPulse = keyframes`
  0% {
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.5), 0 0 20px rgba(100, 182, 255, 0.3);
  }
  50% {
    text-shadow: 0 0 20px rgba(100, 182, 255, 0.8), 0 0 30px rgba(100, 182, 255, 0.6), 0 0 40px rgba(100, 182, 255, 0.4);
  }
  100% {
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.5), 0 0 20px rgba(100, 182, 255, 0.3);
  }
`;

// Animação para a borda holográfica
const holographicBorder = keyframes`
  0% {
    border-image-source: linear-gradient(45deg, rgba(100, 182, 255, 0.8), rgba(0, 255, 204, 0.8));
  }
  25% {
    border-image-source: linear-gradient(90deg, rgba(0, 255, 204, 0.8), rgba(255, 0, 255, 0.8));
  }
  50% {
    border-image-source: linear-gradient(135deg, rgba(255, 0, 255, 0.8), rgba(255, 255, 0, 0.8));
  }
  75% {
    border-image-source: linear-gradient(180deg, rgba(255, 255, 0, 0.8), rgba(100, 182, 255, 0.8));
  }
  100% {
    border-image-source: linear-gradient(225deg, rgba(100, 182, 255, 0.8), rgba(0, 255, 204, 0.8));
  }
`;

// Animação para linhas tecnológicas decorativas
const techLineFlash = keyframes`
  0% {
    opacity: 0.3;
    transform: scaleX(0.8);
  }
  50% {
    opacity: 0.8;
    transform: scaleX(1);
  }
  100% {
    opacity: 0.3;
    transform: scaleX(0.8);
  }
`;

// Animação para elementos decorativos de dados
const dataPointPulse = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0.4;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: scale(0.8);
    opacity: 0.4;
  }
`;

// Animação para entrada do valor por lead
const leadValueEntry = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px) scale(0.8);
    clip-path: inset(0 50% 0 50%);
    filter: blur(10px);
  }
  30% {
    opacity: 0.5;
    transform: translateY(10px) scale(0.9);
    clip-path: inset(0 30% 0 30%);
    filter: blur(5px);
  }
  60% {
    opacity: 0.8;
    transform: translateY(0) scale(1);
    clip-path: inset(0 10% 0 10%);
    filter: blur(2px);
  }
  75% {
    clip-path: inset(0 0 0 0);
    filter: blur(0);
  }
  85% {
    transform: translateY(0) scale(1.05);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// Animação para destacar o valor
const leadValueHighlight = keyframes`
  0% {
    text-shadow: 0 0 10px rgba(0, 255, 204, 0.6);
  }
  50% {
    text-shadow: 0 0 25px rgba(0, 255, 204, 0.9), 0 0 40px rgba(0, 255, 204, 0.4);
  }
  100% {
    text-shadow: 0 0 10px rgba(0, 255, 204, 0.6);
  }
`;

// Animação de pulso da borda
const leadBorderPulse = keyframes`
  0% {
    box-shadow: 0 0 0 1px rgba(0, 255, 204, 0.3);
  }
  50% {
    box-shadow: 0 0 0 2px rgba(0, 255, 204, 0.6), 0 0 20px rgba(0, 255, 204, 0.4);
  }
  100% {
    box-shadow: 0 0 0 1px rgba(0, 255, 204, 0.3);
  }
`;

const SlideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 20px;
  overflow: hidden;
  position: relative;
  cursor: ${props => !props.showFullContent ? 'pointer' : 'default'};
  
  /* Removendo o background sólido */
  background: transparent;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(45deg, rgba(0, 0, 0, 0.7), rgba(10, 20, 40, 0.7)),
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(100, 182, 255, 0.05) 2px,
        rgba(100, 182, 255, 0.05) 4px
      );
    z-index: -1;
    opacity: 0.8; /* Reduzindo a opacidade para maior transparência */
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 200%;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(100, 182, 255, 0.05) 50%,
      transparent 100%
    );
    animation: ${scanLine} 8s linear infinite;
    z-index: -1;
    pointer-events: none;
  }
`;

const SlideTitle = styled.h2`
  font-family: 'Orbitron', sans-serif;
  color: #64B6FF;
  margin-bottom: 30px;
  text-align: center;
  letter-spacing: 2px;
  font-size: 1.8rem;
  text-transform: uppercase;
  position: absolute;
  top: 30px;
  left: 0;
  right: 0;
  text-shadow: 0 0 10px rgba(100, 182, 255, 0.5);
  background: transparent; /* Garantindo fundo transparente */
  
  &::before {
    content: '//';
    color: rgba(100, 182, 255, 0.5);
    margin-right: 10px;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 150px;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.8), transparent);
  }

  /* Animation */
  opacity: 0;
  animation: ${slideInFromTop} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  animation-delay: 0.2s;
`;

const ContentPreview = styled.div`
  max-width: 80%;
  width: 80%;
  margin: 0 auto;
  padding: 30px 40px;
  border: 1px solid rgba(100, 182, 255, 0.3);
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.2);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.2);
  backdrop-filter: blur(10px);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.8), transparent);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.8), transparent);
  }
  
  opacity: 0;
  animation: ${scaleIn} 1s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  animation-delay: 0.5s;
  
  /* Scan line effect */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(to bottom, 
      rgba(100, 182, 255, 0), 
      rgba(100, 182, 255, 0.2), 
      rgba(100, 182, 255, 0));
    z-index: 10;
    animation: ${scanLine} 6s linear infinite;
    animation-delay: 2s;
  }
`;

// Definição do componente CheckoutContainer
const CheckoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  position: relative;
`;

const AnimatedLogoContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  
  .tech-line {
    position: absolute;
    width: 1px;
    height: 50px;
    background: linear-gradient(to bottom, rgba(100, 182, 255, 0.8), transparent);
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  /* Animation */
  opacity: 0;
  transform: translateY(-20px);
  animation: ${slideInFromTop} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  animation-delay: 1s;
`;

const StyledProductInfo = styled(ProductInfo)`
  margin: 0 auto;
  text-align: center;
  max-width: 600px;
  
  ${PriceHighlight} {
    font-size: 2.2rem;
    margin-bottom: 0.5rem;
    opacity: 0;
    animation: ${fadeIn} 0.8s ease forwards;
    animation-delay: 1.6s;
  }
  
  ${PlatformRequirements} {
    margin-top: 1rem;
    opacity: 0;
    animation: ${fadeIn} 0.8s ease forwards;
    animation-delay: 2.4s;
  }
  
  ${PlatformTitle} {
    margin-bottom: 1.5rem;
    font-size: 1rem;
  }
`;

const LeadCostHighlight = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.2rem;
  color: #00FFCC;
  text-shadow: 0 0 15px rgba(0, 255, 204, 0.6);
  margin-top: 30px;
  margin-bottom: 10px;
  padding: 12px 25px;
  border-radius: 8px;
  background: rgba(0, 255, 204, 0.05);
  border: 1px solid rgba(0, 255, 204, 0.2);
  position: relative;
  /* Adicionando as novas animações */
  animation: 
    ${leadValueEntry} 1.2s cubic-bezier(0.23, 1, 0.32, 1) forwards,
    ${leadValueHighlight} 3s infinite 1.2s,
    ${textGlowPulse} 4s infinite 2s;
  opacity: 0; /* Começar invisível */
  transform-origin: center;
  
  &::before {
    content: '';
    position: absolute;
    top: -3px;
    left: -3px;
    right: -3px;
    bottom: -3px;
    border: 1px solid rgba(0, 255, 204, 0.3);
    border-radius: 10px;
    animation: ${leadBorderPulse} 2.5s infinite;
    z-index: -1;
  }
  
  /* Linhas de dados digitais decorativas */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(45deg, transparent 65%, rgba(0, 255, 204, 0.1) 75%, transparent 85%);
    background-size: 200% 200%;
    border-radius: 8px;
    animation: ${dataFlow} 3s linear infinite;
    z-index: -1;
  }
`;

const AnimatedServiceCard = styled(ServiceCard)`
  padding: 10px;
  opacity: 0;
  animation: ${scaleIn} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
`;

const ServiceLogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  margin-bottom: 5px;
`;

const ServiceLogo = styled.img`
  max-width: 40px;
  max-height: 40px;
  object-fit: contain;
  background: transparent;
  padding: 0;
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.3);
`;

const ButtonPreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(90deg, #0080FF, #00A8FF);
  color: white;
  font-family: 'Orbitron', sans-serif;
  padding: 12px 30px;
  border-radius: 30px;
  margin-top: 20px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 0 15px rgba(0, 160, 255, 0.5);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      to bottom right,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.1) 100%
    );
    transform: rotate(45deg);
    z-index: 1;
    transition: all 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 0 20px rgba(0, 160, 255, 0.7);
    
    &::before {
      left: 100%;
    }
  }
  
  /* Animation */
  opacity: 0;
  animation: ${slideInFromBottom} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  animation-delay: 3s;
`;

const ButtonText = styled.span`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    margin-right: 10px;
  }
`;

const DataGraphic = styled.div`
  position: absolute;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  border: 1px solid rgba(100, 182, 255, 0.3);
  top: -50px;
  right: -50px;
  overflow: hidden;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at center, transparent 30%, rgba(0, 10, 20, 0.7) 100%),
      linear-gradient(90deg, rgba(0, 0, 0, 0.2) 1px, transparent 1px),
      linear-gradient(0deg, rgba(0, 0, 0, 0.2) 1px, transparent 1px);
    background-size: 100%, 10px 10px, 10px 10px;
    z-index: 1;
  }
`;

// Nova animação para entrada dos itens revelados
const revealFadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(24px) scale(0.96);
    filter: blur(6px);
  }
  60% {
    opacity: 0.7;
    filter: blur(2px);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
`;

const AnimatedReveal = styled.div`
  animation: ${revealFadeIn} 0.7s cubic-bezier(0.23, 1, 0.32, 1);
`;

// Novo componente para a visualização inicial
const InitialPriceView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  position: relative;
  border-radius: 15px;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(100, 182, 255, 0.2);
  box-shadow: 0 0 30px rgba(100, 182, 255, 0.1);
  max-width: 80%;
  width: 80%;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: -1;
    border-radius: 15px;
  }
  
  &::before {
    background: 
      repeating-linear-gradient(
        -45deg,
        transparent,
        transparent 20px,
        rgba(100, 182, 255, 0.03) 20px,
        rgba(100, 182, 255, 0.03) 40px
      );
  }
  
  &::after {
    background: linear-gradient(
      135deg,
      rgba(100, 182, 255, 0.1) 0%,
      transparent 100%
    );
    filter: blur(10px);
  }
`;

// Componentes estilizados para a visualização inicial
const InitialPriceHighlight = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 5.5rem;
  font-weight: 700;
  color: transparent;
  background: linear-gradient(45deg, #64B6FF, #00FFCC, #00FFFF);
  background-size: 200% 200%;
  background-clip: text;
  -webkit-background-clip: text;
  text-shadow: 0 0 32px rgba(100, 182, 255, 0.7);
  margin-bottom: 30px;
  position: relative;
  animation: 
    ${slideInFromTop} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
    ${dataFlow} 8s linear infinite;
  
  span {
    font-size: 2rem;
    margin-left: 5px;
    opacity: 0.9;
    display: block;
    text-align: center;
    color: #64B6FF;
  }
  
  &::before, &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 50px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(100, 182, 255, 0.5), transparent);
  }
  
  &::before {
    left: -70px;
    transform: translateY(-50%);
  }
  
  &::after {
    right: -70px;
    transform: translateY(-50%);
  }
`;

const InitialCreditHighlight = styled.div`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.2rem;
  color: #00FFCC;
  text-shadow: 0 0 15px rgba(0, 255, 204, 0.6);
  margin-top: 15px;
  padding: 15px 30px;
  border-radius: 10px;
  background: rgba(0, 255, 204, 0.05);
  border: 1px solid rgba(0, 255, 204, 0.2);
  position: relative;
  animation: 
    ${slideInFromBottom} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards,
    ${textGlowPulse} 4s infinite;
  animation-delay: 0.3s;
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border: 1px solid rgba(0, 255, 204, 0.3);
    border-radius: 12px;
    animation: ${glowPulse} 2s infinite;
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(45deg, transparent 65%, rgba(0, 255, 204, 0.1) 75%, transparent 85%);
    background-size: 200% 200%;
    border-radius: 10px;
    animation: ${dataFlow} 3s linear infinite;
    z-index: -1;
  }
`;

const ClickPrompt = styled.div`
  margin-top: 30px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2rem;
  position: relative;
  padding: 10px 20px;
  border-radius: 30px;
  background: rgba(100, 182, 255, 0.1);
  border: 1px dashed rgba(100, 182, 255, 0.3);
  animation: ${pulseAnimation} 2s infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 30px;
    z-index: -1;
    background: radial-gradient(
      circle at center,
      rgba(100, 182, 255, 0.2) 0%,
      transparent 70%
    );
    animation: ${glowPulse} 2s infinite;
  }
  
  &::after {
    content: '↓';
    position: absolute;
    bottom: -25px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(100, 182, 255, 0.7);
    animation: ${pulseAnimation} 1.5s infinite;
    animation-delay: 0.5s;
  }
`;

// Partículas tecnológicas decorativas
const ParticleContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  pointer-events: none;
  z-index: -1;
`;

const Particle = styled.div`
  position: absolute;
  width: ${props => props.size || '4px'};
  height: ${props => props.size || '4px'};
  background: ${props => props.color || 'rgba(100, 182, 255, 0.5)'};
  border-radius: 50%;
  top: ${props => props.top};
  left: ${props => props.left};
  opacity: ${props => props.opacity || 0.5};
  animation: ${floatingParticles} ${props => props.duration || '8s'} infinite;
  animation-delay: ${props => props.delay || '0s'};
  box-shadow: 0 0 5px ${props => props.color || 'rgba(100, 182, 255, 0.5)'};
`;

const ImportantTag = styled.div`
  position: relative;
  margin-bottom: 12px;
  display: inline-block;
  padding: 5px 15px 5px 10px;
  color: #64B6FF;
  font-weight: 600;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.9rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  background: rgba(100, 182, 255, 0.08);
  border-radius: 4px;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(to bottom, #64B6FF, rgba(100, 182, 255, 0.5));
  }
  
  &::after {
    content: '';
    position: absolute;
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: rgba(100, 182, 255, 0.8);
    box-shadow: 0 0 5px rgba(100, 182, 255, 0.8);
    animation: ${dataPointPulse} 2s infinite;
  }
`;

const TechDecoration = styled.div`
  position: absolute;
  pointer-events: none;
  
  &.top-left {
    top: 15px;
    left: 15px;
    width: 40px;
    height: 40px;
    border-top: 1px solid rgba(100, 182, 255, 0.4);
    border-left: 1px solid rgba(100, 182, 255, 0.4);
    border-radius: 5px 0 0 0;
  }
  
  &.bottom-right {
    bottom: 15px;
    right: 15px;
    width: 40px;
    height: 40px;
    border-bottom: 1px solid rgba(100, 182, 255, 0.4);
    border-right: 1px solid rgba(100, 182, 255, 0.4);
    border-radius: 0 0 5px 0;
  }
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: rgba(100, 182, 255, 0.6);
    box-shadow: 0 0 5px rgba(100, 182, 255, 0.6);
  }
  
  &.top-left::before {
    top: -2.5px;
    left: -2.5px;
  }
  
  &.top-left::after {
    bottom: -2.5px;
    right: -2.5px;
  }
  
  &.bottom-right::before {
    top: -2.5px;
    left: -2.5px;
  }
  
  &.bottom-right::after {
    bottom: -2.5px;
    right: -2.5px;
    animation: ${dataPointPulse} 2s infinite;
  }
`;

const TitleHighlight = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.8rem;
  font-weight: 700;
  color: #64B6FF;
  margin-bottom: 24px;
  text-align: center;
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;
  animation: ${slideInFromTop} 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  animation-delay: 0.2s;
`;

const UsagePrices = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 32px;
`;

const UsageItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  justify-content: space-between;
  &.move-down {
    margin-top: 64px;
  }
`;

const UsageValue = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.8rem;
  font-weight: 700;
  color: #00FFCC;
  margin-right: 16px;
`;

const UsageLabel = styled.span`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.8rem;
  color: #64B6FF;
  opacity: 0.85;
`;

const UsageIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.6rem;
  color: #00FFCC;
  margin-left: 16px;
`;

/* -------------------- NOVOS COMPONENTES PARA A MENSALIDADE -------------------- */
const MonthlyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const MonthlyPrice = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.4rem;
  font-weight: 700;
  color: #00FFCC;
  text-shadow: 0 0 12px rgba(0, 255, 204, 0.6);
  span {
    font-size: 1.4rem;
    font-weight: 400;
    margin-left: 8px;
    color: #64B6FF;
  }
`;

const MonthlyFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FeatureItem = styled.li`
  font-family: 'Orbitron', sans-serif;
  color: #64B6FF;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
  svg {
    color: #00FFCC;
    font-size: 0.9rem;
  }
`;

const PresentationSlide22 = () => {
  const { lang } = useLanguage();
  const t = getTranslation('slide22', lang);
  const navigate = useNavigate();
  const location = useLocation();
  const [showFullContent, setShowFullContent] = useState(false);

  // Detecta se está na rota /presentation ou /apresentacao
  const isEnglishRoute = location.pathname.startsWith('/presentation');
  const baseRoute = isEnglishRoute ? '/presentation' : '/apresentacao';

  // Array de partículas decorativas
  const particles = [
    { size: '5px', color: 'rgba(100, 182, 255, 0.7)', top: '15%', left: '10%', duration: '9s', delay: '0s', opacity: 0.7 },
    { size: '3px', color: 'rgba(0, 255, 204, 0.6)', top: '25%', left: '20%', duration: '10s', delay: '0.5s', opacity: 0.6 },
    { size: '6px', color: 'rgba(100, 182, 255, 0.5)', top: '60%', left: '85%', duration: '12s', delay: '1s', opacity: 0.5 },
    { size: '4px', color: 'rgba(0, 255, 204, 0.7)', top: '80%', left: '30%', duration: '11s', delay: '1.5s', opacity: 0.7 },
    { size: '7px', color: 'rgba(100, 182, 255, 0.6)', top: '40%', left: '70%', duration: '9s', delay: '2s', opacity: 0.6 },
    { size: '3px', color: 'rgba(0, 255, 255, 0.8)', top: '20%', left: '80%', duration: '8s', delay: '2.5s', opacity: 0.8 },
    { size: '5px', color: 'rgba(100, 182, 255, 0.7)', top: '70%', left: '10%', duration: '10s', delay: '3s', opacity: 0.7 },
    { size: '4px', color: 'rgba(0, 255, 204, 0.6)', top: '35%', left: '40%', duration: '11s', delay: '3.5s', opacity: 0.6 },
    { size: '6px', color: 'rgba(100, 182, 255, 0.5)', top: '85%', left: '60%', duration: '12s', delay: '4s', opacity: 0.5 },
    { size: '3px', color: 'rgba(0, 255, 255, 0.7)', top: '15%', left: '90%', duration: '9s', delay: '4.5s', opacity: 0.7 },
  ];

  const handleSlideClick = () => {
    if (!showFullContent) {
      // Primeiro clique: mostra o conteúdo completo
      setShowFullContent(true);
    } else {
      // Segundo clique: navega para o slide 22a
      navigate(`${baseRoute}/22a`);
    }
  };

  return (
    <SlideWrapper onClick={handleSlideClick} showFullContent={showFullContent}>
      <ParticleContainer>
        {particles.map((particle, index) => (
          <Particle 
            key={index}
            size={particle.size}
            color={particle.color}
            top={particle.top}
            left={particle.left}
            duration={particle.duration}
            delay={particle.delay}
            opacity={particle.opacity}
          />
        ))}
      </ParticleContainer>
      {!showFullContent ? (
        <InitialPriceView>
          <InitialPriceHighlight>{t.currency} {t.prices.implementation}<span>{t.implementationFee}</span></InitialPriceHighlight>
        </InitialPriceView>
      ) : (
        <ContentPreview>
          <MonthlyContainer>
            <TitleHighlight>{t.monthly}</TitleHighlight>
            <MonthlyPrice>
              {t.currency} {t.prices.monthly} <span>{t.perMonth}</span>
            </MonthlyPrice>
            <ImportantTag>{t.contractInfo}</ImportantTag>
            <MonthlyFeatures>
              <FeatureItem><FontAwesomeIcon icon={faCheck} /> {t.features.visitGeneration}</FeatureItem>
              <FeatureItem><FontAwesomeIcon icon={faCheck} /> {t.features.adScript}</FeatureItem>
              <FeatureItem><FontAwesomeIcon icon={faCheck} /> {t.features.trafficManagement}</FeatureItem>
              <FeatureItem><FontAwesomeIcon icon={faCheck} /> {t.features.scheduling}</FeatureItem>
            </MonthlyFeatures>
          </MonthlyContainer>
        </ContentPreview>
      )}
    </SlideWrapper>
  );
};

export default PresentationSlide22;
